import { slideUp, slideToggle } from "../helpers/Animations.js";

/**
 * Handles collapsible spoller blocks.
 *
 * @version 1.1.1
 */
export default class Spollers {
    /**
     * Creates a spollers instance.
     *
     * @param {Object} options
     * @param {string} [options.containerSelector]
     * @param {string} [options.spollerSelector]
     * @param {boolean} [options.oneAtATime]
     */
    constructor(options = {}) {
        this.containerSelector = options.containerSelector || "[data-spollers]";
        this.spollerSelector = options.spollerSelector || "[data-spoller]";
        this.containers = Array.from(document.querySelectorAll(this.containerSelector));
        this.oneAtATime = options.oneAtATime || false;
    }

    /**
     * Initializes all spoller containers.
     *
     * @returns {void}
     */
    init() {
        if (!this.containers.length) return;

        this.containers.forEach((container) => {
            const params = container.dataset.spollers;

            if (params) {
                const [value, type = "max"] = params.split(",").map((i) => i.trim());
                const mediaQuery = window.matchMedia(`(${type}-width: ${value}px)`);

                const handleMediaChange = (e) => {
                    if (e.matches) {
                        this.initContainer(container);
                        this.setBodies(container, true);
                    } else {
                        this.destroyContainer(container);
                    }
                };

                mediaQuery.addEventListener("change", handleMediaChange);
                handleMediaChange(mediaQuery);
            } else {
                this.initContainer(container);
                this.setBodies(container, true);
            }
        });
    }

    /**
     * Initializes one spoller container.
     *
     * @param {HTMLElement} container
     * @returns {void}
     */
    initContainer(container) {
        container.classList.add("_init");
        container._oneAtATime = container.hasAttribute("data-one-spoller") || this.oneAtATime;

        const spollers = Array.from(container.querySelectorAll(this.spollerSelector));
        spollers.forEach((spoller) => this.restoreState(spoller));

        if (!container._spollerEventsBound) {
            container.addEventListener("click", (e) => this.onClick(e, container));
            container._spollerEventsBound = true;
        }
    }

    /**
     * Restores a spoller state from localStorage.
     *
     * @param {HTMLElement} spoller
     * @returns {void}
     */
    restoreState(spoller) {
        const id = spoller.id;
        if (!id) return;
        if (localStorage.getItem(id) === "_open") {
            spoller.parentElement.classList.add("_open");
        }
    }
    /**
     * Handles spoller clicks.
     *
     * @param {Event} e
     * @param {HTMLElement} container
     * @returns {void}
     */
    onClick(e, container) {
        if (!container.classList.contains("_init")) return;
        const target = e.target.closest(this.spollerSelector);
        if (!target) return;
        e.preventDefault();

        const parent = target.parentElement;
        const isActive = parent.classList.contains("_open");

        if (container._oneAtATime && !isActive) {
            this.closeAll(container, target);
        }

        parent.classList.toggle("_open");

        const body = target.nextElementSibling;
        if (body) slideToggle(body, 300);

        this.saveState(target);
    }

    /**
     * Closes active spollers inside a container.
     *
     * @param {HTMLElement} container
     * @param {HTMLElement|null} except - Spoller that should remain open.
     * @returns {void}
     */
    closeAll(container, except = null) {
        const spollers = Array.from(container.querySelectorAll(this.spollerSelector));
        spollers.forEach((spoller) => {
            if (except && spoller === except) return;

            const parent = spoller.parentElement;
            if (parent.classList.contains("_open")) {
                parent.classList.remove("_open");
                const body = spoller.nextElementSibling;
                if (body) slideUp(body, 300);

                const id = spoller.id;
                if (id) localStorage.removeItem(id);
            }
        });
    }

    /**
     * Saves or removes spoller state in localStorage.
     *
     * @param {HTMLElement} spoller
     * @returns {void}
     */
    saveState(spoller) {
        const id = spoller.id;
        if (!id) return;
        if (spoller.parentElement.classList.contains("_open")) {
            localStorage.setItem(id, "_open");
        } else {
            localStorage.removeItem(id);
        }
    }

    /**
     * Sets initial spoller body visibility.
     *
     * @private
     * @param {HTMLElement} container
     * @param {boolean} hide - Whether inactive bodies should be hidden.
     * @returns {void}
     */
    setBodies(container, hide = true) {
        const spollers = Array.from(container.querySelectorAll(this.spollerSelector));
        spollers.forEach((spoller) => {
            const body = spoller.nextElementSibling;
            if (!body) return;
            if (hide) {
                body.hidden = !spoller.parentElement.classList.contains("_open");
            } else {
                body.hidden = false;
            }
        });
    }

    /**
     * Destroys container state when media query doesn't match.
     *
     * @param {HTMLElement} container
     * @returns {void}
     */
    destroyContainer(container) {
        container.classList.remove("_init");
        const spollers = Array.from(container.querySelectorAll(this.spollerSelector));
        spollers.forEach((spoller) => {
            spoller.parentElement.classList.remove("_open");
            const body = spoller.nextElementSibling;
            if (body) {
                body.hidden = false;
                body.style.display = "";
            }
        });
    }
}
